/**
 * 购物车状态管理
 */
import { defineStore } from 'pinia'
import { getCurrencySymbol, calculateTotal, calculateTotalRMB } from '@/common/utils/currency'

export const useCartStore = defineStore('cart', {
  state: () => ({
    sessionKey: null, // 区分不同登录会话，确保每次登录开始于干净购物车
    menuSignature: null, // 区分不同菜单，避免新菜单沿用旧购物车
    items: [], // 购物车商品列表
    currency: 'EUR', // 当前货币
    exchangeRate: 1.0 // 汇率
  }),

  getters: {
    /**
     * 购物车商品总数
     */
    totalCount: (state) => {
      return state.items.reduce((total, item) => total + item.quantity, 0)
    },

    /**
     * 购物车总价（原始货币）
     */
    totalPrice: (state) => {
      return calculateTotal(state.items, 'price', 'quantity')
    },

    /**
     * 购物车总价（人民币）
     */
    totalPriceRMB: (state) => {
      return calculateTotalRMB(state.items, state.exchangeRate, 'price', 'quantity')
    },

    /**
     * 是否有商品
     */
    hasItems: (state) => state.items.length > 0,

    /**
     * 获取商品数量
     */
    getItemQuantity: (state) => (dishName) => {
      const item = state.items.find(item => item.name === dishName)
      return item ? item.quantity : 0
    },
    
    /**
     * 格式化总价显示
     */
    formattedTotalPrice: (state) => {
      const symbol = getCurrencySymbol(state.currency)
      const price = state.totalPrice.toFixed(2)
      return `${symbol}${price}`
    },
    
    /**
     * 格式化人民币总价
     */
    formattedTotalPriceRMB() {
      return `¥${this.totalPriceRMB.toFixed(2)}`
    }
  },

  actions: {
    /**
     * 设置货币信息
     */
    setCurrency(currency, exchangeRate) {
      this.currency = currency
      this.exchangeRate = exchangeRate
    },

    /**
     * 同步菜单标识，菜单变化时清空购物车
     * @returns {boolean} 是否因菜单变化清空了购物车
     */
    syncMenuSignature(signature) {
      if (!signature) {
        this.menuSignature = null
        return false
      }

      const changed = this.menuSignature !== signature
      if (changed) {
        this.clearCart()
      }
      this.menuSignature = signature
      return changed
    },

    /**
     * 添加商品到购物车
     */
    addItem(dish) {
      const existingItem = this.items.find(item => item.name === dish.name)
      
      if (existingItem) {
        existingItem.quantity++
      } else {
        this.items.push({
          ...dish,
          quantity: 1,
          id: Date.now().toString() // 生成唯一ID
        })
      }
    },

    /**
     * 减少商品数量
     */
    reduceItem(dishName) {
      const itemIndex = this.items.findIndex(item => item.name === dishName)
      
      if (itemIndex > -1) {
        const item = this.items[itemIndex]
        if (item.quantity > 1) {
          item.quantity--
        } else {
          this.items.splice(itemIndex, 1)
        }
      }
    },

    /**
     * 设置商品数量
     */
    setItemQuantity(dishName, quantity) {
      if (quantity <= 0) {
        this.removeItem(dishName)
        return
      }

      const item = this.items.find(item => item.name === dishName)
      if (item) {
        item.quantity = quantity
      }
    },

    /**
     * 移除商品
     */
    removeItem(dishName) {
      const itemIndex = this.items.findIndex(item => item.name === dishName)
      if (itemIndex > -1) {
        this.items.splice(itemIndex, 1)
      }
    },

    /**
     * 清空购物车
     */
    clearCart() {
      this.items = []
    },
    
    /**
     * 重置购物车状态（用于账号切换）
     */
    resetCartState(sessionKey = null) {
      this.$reset()
      this.sessionKey = sessionKey
    },

    /**
     * 校验购物车会话，如果变更则自动清空
     */
    ensureSession(sessionKey) {
      const normalizedSession = sessionKey || null
      if (this.sessionKey !== normalizedSession) {
        this.resetCartState(normalizedSession)
      }
    },

    /**
     * 从菜单数据初始化购物车
     */
    initFromMenu(menuData) {
      if (menuData.metadata) {
        this.currency = menuData.metadata.currency || 'EUR'
        this.exchangeRate = menuData.metadata.exchange_rate || 1.0
      }
    },
    
    /**
     * 获取购物车摘要
     */
    getCartSummary() {
      return {
        items: this.items.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          total: item.price * item.quantity
        })),
        totalCount: this.totalCount,
        totalPrice: this.totalPrice,
        totalPriceRMB: this.totalPriceRMB,
        currency: this.currency
      }
    }
  },

  // 持久化配置 - 使用 pinia-plugin-persistedstate
  persist: {
    key: 'cart-storage',
    storage: localStorage,
    pick: ['sessionKey', 'menuSignature', 'items', 'currency', 'exchangeRate'] // 只持久化需要的字段
  }
})
